import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import PreferredBreederBadge from 'dpl/breeder_preferred_qualifications/components/PreferredBreederBadge';
import { STATUS } from 'dpl/breeder_preferred_qualifications/utils/constants';
import CardMediaCarousel from 'dpl/breeder_search_results/components/CardMediaCarousel';
import { TRANSPORT_TYPES } from 'dpl/breeder_search_results/utils/constants';
import ClickableDiv from 'dpl/common/components/ClickableDiv';
import Icon from 'dpl/common/components/Icon';
import Badge from 'dpl/common/design_system/Badge';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import SmartImage from 'dpl/components/SmartImage';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import FavoriteButton from 'dpl/favorites/components/FavoriteButton';
import { FOLLOWABLE_TYPES } from 'dpl/favorites/utils/constants';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import { openPuppyPage } from 'dpl/puppy_page/utils/helpers';
import { isNumeric } from 'dpl/shared/form/utils';
import { capitalize, flatten } from 'dpl/shared/utils';
import { PuppyCardPropTypes } from 'dpl/shared/utils/customPropTypes';
import { getAnalyticsOrigin } from 'dpl/shared/utils/getAnalyticsOrigin';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import TransportationModal from 'dpl/modals/TransportationModal';
import {
  FILTER_POSTAL_CODE,
  FILTER_LOCATION_NAME,
  FILTER_GENDER
} from 'dpl/constants/query_params';

import useSearchResultsContext from '../hooks/useSearchResultsContext';
import { getReviewsUrl } from '../utils';

const IS_LT_MD = isLessThanBreakpoint('md');
const { AIR, GROUND } = TRANSPORT_TYPES;
const NEARBY_MAX_DISTANCE = 250;

const transportDefaultPropPickupOptions = [];
/** @param {any} props */
function Transport({
  deliveryMethods,
  breederFirstName = null,
  puppyName = null,
  pickupOptions = transportDefaultPropPickupOptions,
  distance = null
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const transportTypes = Object.keys(deliveryMethods || {});

  const hasTransportation =
    transportTypes.includes(AIR) || transportTypes.includes(GROUND);

  function handleOpenClick(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  }

  function handleCloseClick(e) {
    e?.stopPropagation?.();
    setIsModalOpen(false);
  }

  const mileOrMiles = distance === 1 ? 'mile' : 'miles';

  const milesAway = isNumeric(distance)
    ? `${distance} ${mileOrMiles} away`
    : '';

  const isNearby = distance <= NEARBY_MAX_DISTANCE;

  return (
    <div className="PuppySearchResultsTransport font-12 tertiary">
      {isNearby && milesAway ? (
        <p>{milesAway}</p>
      ) : (
        <button
          type="button"
          className="flex items-center pointer underline"
          tabIndex="0"
          onClick={handleOpenClick}
        >
          {hasTransportation ? 'Drop off to you' : 'Meet breeder for pickup'}
        </button>
      )}
      {isModalOpen && (
        <TransportationModal
          onCloseClick={handleCloseClick}
          deliveryMethods={deliveryMethods}
          breederFirstName={breederFirstName}
          puppyName={puppyName}
          pickupOptions={pickupOptions}
          distance={distance}
        />
      )}
    </div>
  );
}

Transport.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  deliveryMethods: PropTypes.object.isRequired,
  breederFirstName: PropTypes.string,
  puppyName: PropTypes.string,
  distance: PropTypes.number,
  pickupOptions: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      distance: PropTypes.number,
      key: PropTypes.string
    })
  )
};

function setOrigin({ analytics }) {
  const { origin_view: originView } = getAnalyticsOrigin();

  setAnalyticsOrigin({
    originView,
    originViewFeature: ANALYTICS_VIEW_FEATURES.PUPPY_CARD,
    originViewFeatureLocation: analytics?.view_feature_location
  });
}

const cardDefaultPropTrackInView = () => {};
const cardDefaultPropSmartImageProps = {};
/** @param {any} props */
export default function Card({
  puppy,
  analytics,
  className = null,
  trackInView = cardDefaultPropTrackInView,
  onFavoriteChange = null,
  bordered = false,
  smartImageProps = cardDefaultPropSmartImageProps,
  breedName = null,
  compact = false
}) {
  const { isLoggedIn, isBuyer } = useCurrentUser();

  const { locationTitle, breedId } = useSelector(({ server }) => server);

  const {
    id,
    name,
    profile_photo_id: profilePhotoId,
    profile_photo_url: profilePhotoUrl,
    distance_in_miles: distance,
    ranking_distance_in_miles: meetupDistance,
    breeder_profile: breederProfile,
    media_files: mediaFiles,
    approved_testimonial_count: reviewsCount,
    ready_for_adoption_date_v2: readyDateDescription,
    reserved
  } = puppy;

  const {
    id: breederProfileId,
    url: breederProfileUrl,
    name: breederProfileProgramName,
    display_name: breederProfileDisplayName,
    delivery_methods: deliveryMethods,
    pickup_options: pickupOptions,
    first_name: breederFirstName,
    preferred_qualification: preferredQualification,
    profile_photo_url: breederPhotoUrl,
    response_time_description: responseTimeDescription
  } = breederProfile;

  const preferredQualificationStatus = preferredQualification?.status;
  const isPreferredBreeder = preferredQualificationStatus === STATUS.PREFERRED;

  const ageGenderDescription = [
    puppy.age_description,
    capitalize(puppy.gender || '')
  ]
    .filter(Boolean)
    .join(' • ');

  const hasReviews = reviewsCount > 0;

  const favoriteAnalytics = useMemo(
    () => ({
      ...analytics,
      page_number: analytics.page_number || null,
      position: analytics.position || null,
      breeder_profile_id: breederProfileId,
      breeder_name: breederProfileProgramName,
      dog_id: id,
      breed_id: puppy.breed?.id || Number(breedId) || null
    }),
    [id, analytics]
  );

  const computedAnalytics = useMemo(
    () => ({
      ...analytics,
      breeder_preferred_status: preferredQualificationStatus,
      breeder_profile_id: breederProfileId,
      view_feature: ANALYTICS_VIEW_FEATURES.PUPPY_CARD,
      miles_away: distance,
      miles_away_ranking: meetupDistance,
      dog_id: id,
      // A similar breed card is listed under a breed of its own, and only that
      // row carries it. Everywhere else the card renders the breed the page was
      // searched for, and no row carries a `breed` to prefer.
      breed_id: puppy.breed?.id || breedId,
      transportation: flatten(Object.values(deliveryMethods || {})).map(
        method => method.name
      )
    }),
    [id, analytics]
  );

  const trackReviewsLinkClick = useCallback(
    e => {
      e.stopPropagation();

      setOrigin({ analytics });
      sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
        ...computedAnalytics,
        view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.REVIEWS_LINK
      });
    },
    [id, analytics]
  );

  const puppyMediaFiles = useMemo(() => {
    const pupMediaFiles = [...(mediaFiles || [])];
    if (profilePhotoId || pupMediaFiles.length === 0) {
      pupMediaFiles.unshift({
        id: profilePhotoId,
        url: profilePhotoUrl
      });
    }
    return pupMediaFiles;
  }, [mediaFiles, profilePhotoId]);

  const photosWithAlt = useMemo(() => {
    const images = puppyMediaFiles.map(photo => ({
      ...photo,
      alt: locationTitle ? `${name} in ${locationTitle}` : name
    }));

    return images;
  }, [locationTitle, name, puppyMediaFiles]);

  const cardSmartImageProps = {
    Tag: 'div',
    className: classnames('w-100 bg-center cover', {
      'br-none bg-sand': bordered
    }),
    loadingClass: 'bg-light-gray',
    height: IS_LT_MD ? 'calc(100vw - 48px)' : '335px',
    allowContextMenu: true,
    crop: true,
    ...smartImageProps
  };

  const userAvatarBreeder = {
    ...breederProfile,
    profile_photo_url: breederPhotoUrl
  };

  const selectedLocation = useSelector(({ queryParams }) => {
    if (queryParams[FILTER_POSTAL_CODE]) {
      return queryParams[FILTER_POSTAL_CODE];
    }

    if (queryParams[FILTER_LOCATION_NAME]) {
      return decodeURIComponent(queryParams[FILTER_LOCATION_NAME]);
    }

    return null;
  });

  const genderFilter = useSelector(({ queryParams }) => {
    return queryParams[FILTER_GENDER];
  });

  const { searchParams, referrer } = useSearchResultsContext();

  const handleCardClick = useCallback(
    ({ currentTarget, target }) => {
      if (currentTarget !== target && !currentTarget.contains(target)) {
        return;
      }

      setOrigin({ analytics });

      sendRudderstackEvent(
        ANALYTICS_EVENTS.CARD_CLICKED_BY_USER,
        computedAnalytics
      );

      openPuppyPage({
        puppyId: id,
        locName: selectedLocation || locationTitle,
        gender: genderFilter,
        searchParams,
        referrer
      });
    },
    [analytics, computedAnalytics]
  );

  return (
    <InView
      triggerOnce
      onChange={inView => {
        if (inView) trackInView();
      }}
      threshold={0.8}
      className={classnames(
        'PuppySearchResultsCard h-100 db',
        { 'ba b--default br3 bg-white overflow-hidden': bordered },
        className
      )}
    >
      <ClickableDiv
        className="PuppySearchResultsCard__container h-100 w-100 relative z-0"
        onClick={handleCardClick}
      >
        <div
          className={classnames('relative flex flex-column justify-center', {
            'br3 overflow-hidden': !bordered
          })}
        >
          {compact ? (
            <SmartImage
              {...cardSmartImageProps}
              src={photosWithAlt[0]?.url}
              alt={photosWithAlt[0]?.alt}
              lazy
            />
          ) : (
            <CardMediaCarousel
              mediaFiles={photosWithAlt}
              smartImageProps={cardSmartImageProps}
              lazyBuffer={window.outerHeight * 2}
              isPuppyCard
            />
          )}
          <div className="white absolute w-100 top-0 right-0">
            {breedName && (
              <div
                className={classnames(
                  'absolute left-0 w-100',
                  compact ? 'ph2 pv2' : 'ph4 pv4'
                )}
              >
                <span className="dib mw-100 truncate font-12 br-pill bg-black-60 ph2 pv1">
                  {breedName}
                </span>
              </div>
            )}
            <div className="ph4 pv4 absolute right-0">
              {!compact && (!isLoggedIn || isBuyer) && (
                <FavoriteButton
                  analytics={favoriteAnalytics}
                  followableTypeId={id}
                  followableType={FOLLOWABLE_TYPES.DOG}
                  onChange={onFavoriteChange}
                  checkedIconProps={{ name: 'fetch-heart-full-puppy' }}
                  uncheckedIconProps={{
                    name: 'fetch-heart-dark',
                    className: 'default-color o-100'
                  }}
                  puppyName={puppy.name}
                  puppyPhotoUrl={puppy.profile_photo_url}
                  breederFirstName={breederFirstName}
                  breederProfilePhotoUrl={breederPhotoUrl}
                />
              )}
            </div>
          </div>
        </div>
        <div className={classnames({ ph3: bordered })}>
          <div
            className={classnames(
              'flex items-center justify-between',
              compact ? 'mt3 gap-12' : 'mt4 gap-24'
            )}
          >
            <p
              className={classnames('fw-medium truncate font-serif', {
                'font-18': compact,
                'font-24': !compact
              })}
            >
              {puppy.name}
            </p>
            {(reserved || (!compact && readyDateDescription)) && (
              <Badge className="nowrap" color="gray" size="small">
                {reserved ? 'Reserved' : readyDateDescription}
              </Badge>
            )}
          </div>
          {ageGenderDescription && (
            <p
              className={classnames('secondary', {
                'font-12': compact,
                'font-14': !compact
              })}
            >
              {ageGenderDescription}
            </p>
          )}
        </div>
        <div className={classnames({ ph3: bordered })}>
          <div className="flex items-center gap-8 bt b--default mt3 pt2">
            <div className="relative">
              <UserAvatar
                user={userAvatarBreeder}
                size={compact ? 'sm' : 'default'}
              />
              {isPreferredBreeder && (
                <PreferredBreederBadge
                  className="absolute right-0 bottom-0"
                  width={compact ? '12px' : '20px'}
                  height={compact ? '12px' : '20px'}
                  hideTooltip
                />
              )}
            </div>
            <div className="overflow-hidden">
              <span
                className={classnames('w-100 truncate', {
                  'db font-14': compact,
                  'dib font-16': !compact
                })}
              >
                Raised by {breederProfileDisplayName}
              </span>
              <Transport
                className="font-12 tertiary"
                deliveryMethods={deliveryMethods}
                pickupOptions={pickupOptions}
                breederFirstName={breederFirstName}
                puppyName={puppy.name}
                distance={distance}
              />
            </div>
          </div>
          {!compact && (responseTimeDescription || hasReviews) && (
            <div className="inline-flex flex-column gap-4 mt2 secondary">
              {responseTimeDescription && (
                <p className="flex items-center gap-4">
                  <Icon name="fetch-clock" height="20px" width="20px" />
                  <span className="font-14 lh-title">
                    {responseTimeDescription}
                  </span>
                </p>
              )}
              {hasReviews && (
                <a
                  className="flex items-center gap-4"
                  href={getReviewsUrl(breederProfileUrl)}
                  target="_blank"
                  onClick={trackReviewsLinkClick}
                  rel="noreferrer"
                >
                  <Icon name="fetch-review" height="20px" width="20px" />
                  <span className="font-14 lh-title underline">
                    {reviewsCount} {reviewsCount === 1 ? 'story' : 'stories'}
                  </span>
                </a>
              )}
            </div>
          )}
        </div>
      </ClickableDiv>
    </InView>
  );
}

Card.propTypes = PuppyCardPropTypes;
